/**
 * Iter-253 — Creator Network — Talent surface.
 *
 * Single canonical page at /talent/creator-network. Status-aware:
 *
 *   • not applied         → marketing landing + Apply CTA
 *   • pending             → "Application under review" status card
 *   • changes_requested   → Reviewer notes + re-submit form
 *   • approved            → "You're a Verified Creator" dashboard:
 *                           [Preview my profile] [Edit Creator Profile]
 *                           [My Portfolio]       [Social Handles]
 *                           [Performance — coming soon]
 *   • rejected            → Reason + 30d cooldown messaging
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Sparkles, Clock, CheckCircle2, XCircle, AlertTriangle, ArrowRight,
  ExternalLink, Eye, Link as LinkIcon, Trash2, Loader2, Plus, ChevronLeft,
} from 'lucide-react';
import { toast } from 'sonner';


const STATUS_META = {
  pending:            { label: 'Under Review',      color: 'bg-amber-100 text-amber-800 border-amber-300',  icon: Clock },
  approved:           { label: 'Verified Creator',  color: 'bg-emerald-100 text-emerald-800 border-emerald-300', icon: CheckCircle2 },
  rejected:           { label: 'Not Approved',      color: 'bg-rose-100 text-rose-700 border-rose-300',     icon: XCircle },
  changes_requested:  { label: 'Changes Requested', color: 'bg-orange-100 text-orange-800 border-orange-300', icon: AlertTriangle },
};


export default function TalentCreatorNetwork() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [view, setView] = useState('status');   // 'status' | 'apply'

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/creator-applications/me');
      setData(r.data);
      // If they've never applied, default to the marketing landing
      if (!r.data.application) setView('status');
    } catch (e) {
      toast.error('Failed to load Creator Network status');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50">
        <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
      </div>
    );
  }

  const app = data?.application;
  const profile = data?.profile;
  const isApproved = profile?.verified_creator;
  const status = app?.status || (isApproved ? 'approved' : null);
  const statusMeta = status ? STATUS_META[status] : null;

  if (view === 'apply') {
    return (
      <ApplicationForm
        initial={app}
        onBack={() => setView('status')}
        onSubmitted={() => { setView('status'); load(); }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-16 overflow-x-hidden" data-testid="talent-creator-network">
      {/* Header */}
      <div className="bg-gradient-to-br from-purple-600 via-fuchsia-600 to-pink-600 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <button
            onClick={() => navigate('/talent/home')}
            className="text-white/80 hover:text-white text-sm flex items-center gap-1 mb-3 sm:mb-4"
            data-testid="creator-network-back"
          >
            <ChevronLeft className="w-4 h-4" /> Back to dashboard
          </button>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0 flex-1">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold flex items-center gap-2">
                <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" />
                <span className="break-words">iEvntür Creator Network</span>
              </h1>
              <p className="text-white/85 mt-2 text-xs sm:text-sm max-w-xl">
                A vetted community of Influencers and Content Creators that brands
                book directly from inside iEvntür.
              </p>
            </div>
            {statusMeta && (
              <Badge className={`${statusMeta.color} border text-xs flex items-center gap-1.5 px-2.5 py-1 sm:px-3 sm:py-1.5 whitespace-nowrap flex-shrink-0`}>
                <statusMeta.icon className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                {statusMeta.label}
              </Badge>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-3 sm:px-6 -mt-4 sm:-mt-6 space-y-4 sm:space-y-6">
        {/* Status card */}
        {status === 'approved' && <ApprovedDashboard profile={profile} app={app} />}
        {status === 'pending' && <PendingCard app={app} />}
        {status === 'changes_requested' && <ChangesRequestedCard app={app} onEdit={() => setView('apply')} />}
        {status === 'rejected' && <RejectedCard app={app} onReapply={() => setView('apply')} />}
        {!status && <UnappliedLanding onApply={() => setView('apply')} />}
      </div>
    </div>
  );
}


// =========================================================================
//  STATUS CARDS
// =========================================================================

const UnappliedLanding = ({ onApply }) => (
  <Card className="border-purple-200">
    <CardContent className="p-6 space-y-4">
      <h2 className="text-lg font-bold text-slate-900">
        Apply to the Creator Network
      </h2>
      <p className="text-sm text-slate-600">
        Get matched to brand campaigns, surface in the dedicated Creator
        Network search, and unlock the creator-specific dashboard. Applications
        are reviewed by the iEvntür team — usually within 1–3 business days.
      </p>
      <ul className="text-sm text-slate-700 space-y-2 list-disc pl-5">
        <li>Verified <strong>Creator</strong> badge on your public profile</li>
        <li>Direct visibility to brands inside <code>/client/creators</code></li>
        <li>Creator-specific portfolio, social handles, and media kit fields</li>
        <li>Priority on Creator-tagged campaigns (Brand Ambassador, UGC, Content)</li>
      </ul>
      <Button
        onClick={onApply}
        className="bg-purple-600 hover:bg-purple-700 text-white"
        data-testid="creator-network-apply-cta"
      >
        Start application <ArrowRight className="w-4 h-4 ml-1" />
      </Button>
    </CardContent>
  </Card>
);

const PendingCard = ({ app }) => (
  <Card className="border-amber-200 bg-amber-50/40">
    <CardContent className="p-6 space-y-3">
      <div className="flex items-center gap-2 text-amber-800">
        <Clock className="w-5 h-5" />
        <h2 className="text-base font-bold">Application under review</h2>
      </div>
      <p className="text-sm text-slate-700">
        We received your application on{' '}
        <strong>{new Date(app.submitted_at).toLocaleDateString()}</strong>.
        The iEvntür team typically reviews creator applications within 1–3
        business days. You&apos;ll get an email the moment your status changes.
      </p>
    </CardContent>
  </Card>
);

const ChangesRequestedCard = ({ app, onEdit }) => (
  <Card className="border-orange-200 bg-orange-50/40">
    <CardContent className="p-6 space-y-3">
      <div className="flex items-center gap-2 text-orange-800">
        <AlertTriangle className="w-5 h-5" />
        <h2 className="text-base font-bold">A few small updates needed</h2>
      </div>
      {app.reviewer_notes && (
        <div className="text-sm bg-white border border-orange-200 rounded p-3 text-slate-700">
          <span className="text-[10px] uppercase tracking-wider text-orange-700 font-semibold">Reviewer notes</span>
          <p className="mt-1 whitespace-pre-wrap">{app.reviewer_notes}</p>
        </div>
      )}
      <Button onClick={onEdit} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="creator-network-edit-cta">
        Update & re-submit
      </Button>
    </CardContent>
  </Card>
);

const RejectedCard = ({ app, onReapply }) => (
  <Card className="border-rose-200 bg-rose-50/30">
    <CardContent className="p-6 space-y-3">
      <div className="flex items-center gap-2 text-rose-700">
        <XCircle className="w-5 h-5" />
        <h2 className="text-base font-bold">Application not approved</h2>
      </div>
      {app.rejection_reason && (
        <div className="text-sm bg-white border border-rose-200 rounded p-3 text-slate-700">
          <span className="text-[10px] uppercase tracking-wider text-rose-700 font-semibold">Why</span>
          <p className="mt-1 whitespace-pre-wrap">{app.rejection_reason}</p>
        </div>
      )}
      <p className="text-xs text-slate-600">
        You&apos;re welcome to re-apply once you&apos;ve grown your portfolio. We don&apos;t
        enforce a cooldown — apply when you&apos;re ready.
      </p>
      <Button onClick={onReapply} variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="creator-network-reapply-cta">
        Re-apply
      </Button>
    </CardContent>
  </Card>
);

const ApprovedDashboard = ({ profile, app }) => {
  const navigate = useNavigate();
  const ctLabel = {
    influencer: 'Influencer',
    content_creator: 'Content Creator',
    both: 'Influencer + Creator',
  }[profile?.creator_type || app?.creator_type] || 'Creator';

  const tiles = [
    {
      id: 'preview',
      title: 'Preview as a brand sees you',
      desc: 'Open your public Creator Network profile in a new tab.',
      icon: Eye,
      onClick: () => window.open('/talent/me/preview-creator', '_blank'),
      testId: 'creator-tile-preview',
    },
    {
      id: 'edit',
      title: 'Edit creator profile',
      desc: 'Bio, content categories, brands worked with, media kit.',
      icon: Sparkles,
      onClick: () => navigate('/talent/creator-profile'),
      testId: 'creator-tile-edit',
    },
    {
      id: 'socials',
      title: 'Social handles',
      desc: 'Instagram, TikTok, YouTube, Facebook. OAuth sync coming soon.',
      icon: LinkIcon,
      onClick: () => navigate('/talent/creator-profile?section=socials'),
      testId: 'creator-tile-socials',
    },
    {
      id: 'performance',
      title: 'My performance',
      desc: 'Profile views, search impressions and brand inquiries.',
      icon: CheckCircle2,
      onClick: () => navigate('/talent/insights'),
      testId: 'creator-tile-performance',
    },
  ];

  return (
    <>
      <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-cyan-50/40">
        <CardContent className="p-6 space-y-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                <h2 className="text-base font-bold text-emerald-900">You&apos;re a Verified Creator</h2>
              </div>
              <p className="text-xs text-emerald-800/80 mt-1">
                Your <strong>{ctLabel}</strong> profile is live in <code>/client/creators</code>.
              </p>
            </div>
            {app?.reviewed_at && (
              <div className="text-[11px] text-emerald-800/70">
                Approved {new Date(app.reviewed_at).toLocaleDateString()}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="grid sm:grid-cols-2 gap-2.5 sm:gap-3">
        {tiles.map((t) => (
          <button
            key={t.id}
            onClick={t.onClick}
            className="bg-white border border-slate-200 rounded-xl p-3 sm:p-4 text-left hover:border-purple-300 hover:shadow-md transition-all"
            data-testid={t.testId}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="text-sm font-bold text-slate-900 flex items-center gap-1.5">
                  <t.icon className="w-4 h-4 text-purple-600" />
                  {t.title}
                </div>
                <p className="text-[12px] text-slate-500 mt-1">{t.desc}</p>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-400 mt-1 flex-shrink-0" />
            </div>
          </button>
        ))}
      </div>
    </>
  );
};


// =========================================================================
//  APPLICATION FORM
// =========================================================================

const ApplicationForm = ({ initial, onBack, onSubmitted }) => {
  const [creatorType, setCreatorType] = useState(initial?.creator_type || '');
  const [whyBlurb, setWhyBlurb] = useState(initial?.why_blurb || '');
  const [portfolioLinks, setPortfolioLinks] = useState(
    (initial?.portfolio_links && initial.portfolio_links.length > 0)
      ? initial.portfolio_links
      : [{ label: '', url: '' }],
  );
  const [submitting, setSubmitting] = useState(false);

  const addLink = () => {
    if (portfolioLinks.length >= 3) return;
    setPortfolioLinks([...portfolioLinks, { label: '', url: '' }]);
  };
  const removeLink = (i) => {
    setPortfolioLinks(portfolioLinks.filter((_, idx) => idx !== i));
  };
  const updateLink = (i, key, val) => {
    setPortfolioLinks(portfolioLinks.map((l, idx) => (idx === i ? { ...l, [key]: val } : l)));
  };

  const submit = async () => {
    if (!creatorType) { toast.error('Pick a Creator Type'); return; }
    if (!whyBlurb || whyBlurb.length < 20) { toast.error('Tell us a bit more (20+ chars)'); return; }
    const cleanLinks = portfolioLinks.filter((l) => l.label && l.url);
    setSubmitting(true);
    try {
      await api.post('/creator-applications', {
        creator_type: creatorType,
        why_blurb: whyBlurb,
        portfolio_links: cleanLinks,
        sample_content: [],
      });
      toast.success('Application submitted — we&apos;ll review within 1\u20133 business days.');
      onSubmitted();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not submit application');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-16 overflow-x-hidden" data-testid="creator-network-apply">
      <div className="bg-gradient-to-br from-purple-600 to-pink-600 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-5 sm:py-6">
          <button
            onClick={onBack}
            className="text-white/80 hover:text-white text-sm flex items-center gap-1 mb-2 sm:mb-3"
            data-testid="creator-apply-back"
          >
            <ChevronLeft className="w-4 h-4" /> Back
          </button>
          <h1 className="text-lg sm:text-xl font-bold flex items-center gap-2">
            <Sparkles className="w-5 h-5 flex-shrink-0" /> Creator Network Application
          </h1>
          <p className="text-white/80 text-xs sm:text-sm mt-1">
            We review every application by hand. Expect a response in 1–3 business days.
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-3 sm:px-6 -mt-3 sm:-mt-4 space-y-3 sm:space-y-4">
        {/* Creator Type */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Which fits you best?</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {[
              { id: 'influencer',      title: 'Influencer',        desc: 'Audience-driven. Brands book me for reach and on-event promotion.' },
              { id: 'content_creator', title: 'Content Creator',   desc: 'Production-driven. Brands book me for UGC, photography, video, written content.' },
              { id: 'both',            title: 'Both',              desc: 'I create content AND have an audience.' },
            ].map((opt) => (
              <label
                key={opt.id}
                className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  creatorType === opt.id ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-gray-300'
                }`}
                data-testid={`apply-type-${opt.id}`}
              >
                <input
                  type="radio"
                  checked={creatorType === opt.id}
                  onChange={() => setCreatorType(opt.id)}
                  className="mt-1 h-4 w-4 text-purple-600"
                />
                <div>
                  <div className="text-sm font-semibold text-slate-900">{opt.title}</div>
                  <div className="text-xs text-slate-500">{opt.desc}</div>
                </div>
              </label>
            ))}
          </CardContent>
        </Card>

        {/* Why iEvntür blurb */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Why iEvntür?</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={whyBlurb}
              onChange={(e) => setWhyBlurb(e.target.value.slice(0, 280))}
              placeholder="What kind of brand work do you love? What sets your content apart? (20–280 characters)"
              rows={4}
              data-testid="apply-why-blurb"
            />
            <div className="text-[10px] text-slate-500 text-right mt-1">{whyBlurb.length}/280</div>
          </CardContent>
        </Card>

        {/* Portfolio links */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Show us your best work</CardTitle>
            <p className="text-xs text-slate-500 mt-1">
              Up to 3 links — Instagram reels, TikTok permalinks, a YouTube playlist,
              a Google Drive folder, a Notion portfolio. Anything we can click.
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            {portfolioLinks.map((link, i) => (
              <div key={i} className="flex gap-2 items-end flex-wrap sm:flex-nowrap" data-testid={`apply-portfolio-row-${i}`}>
                <div className="flex-1 min-w-[120px]">
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">Label</Label>
                  <Input
                    value={link.label}
                    onChange={(e) => updateLink(i, 'label', e.target.value)}
                    placeholder="e.g. Best Reel"
                    maxLength={64}
                  />
                </div>
                <div className="flex-[2] min-w-[180px]">
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">URL</Label>
                  <Input
                    value={link.url}
                    onChange={(e) => updateLink(i, 'url', e.target.value)}
                    placeholder="https://..."
                    maxLength={512}
                  />
                </div>
                {portfolioLinks.length > 1 && (
                  <button
                    onClick={() => removeLink(i)}
                    className="text-slate-400 hover:text-rose-500 p-1.5 flex-shrink-0"
                    data-testid={`apply-portfolio-remove-${i}`}
                    aria-label="Remove link"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
            {portfolioLinks.length < 3 && (
              <Button
                variant="outline"
                size="sm"
                onClick={addLink}
                data-testid="apply-portfolio-add"
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> Add another link
              </Button>
            )}
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onBack} data-testid="creator-apply-cancel">Cancel</Button>
          <Button
            onClick={submit}
            disabled={submitting}
            className="bg-purple-600 hover:bg-purple-700 text-white"
            data-testid="creator-apply-submit"
          >
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Submitting…</> : <>Submit application <ExternalLink className="w-3.5 h-3.5 ml-1" /></>}
          </Button>
        </div>
      </div>
    </div>
  );
};
